import { Component, OnInit } from '@angular/core';
import { EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { ResetPasswordService, InformacionUsuarioData } from '../../../../core/services/reset-password.service';
import { ResetPasswordFlowContext } from '../../../../core/services/reset-password-flow.context';
import {
  tokenVirtualPasswordValidators,
  ccsw5PasswordValidators,
  passwordHasMinLength,
  passwordHasCcsw5Length,
  passwordHasUpper,
  passwordHasNumber,
  passwordHasSpecial,
  PASSWORD_MAX_LENGTH
} from '../../../../core/validators/password.validators';
import { UiHelperService } from '../../../../services/ui-helper.service';
import { NotificationService } from '../../../../services/notification.service';
import Swal from 'sweetalert2';

interface Certificado {
  cn: string;
  titulo: string;
  nombre: string;
  politica: string;
  /** Serial number mostrado: en CCSW5 = solicitudTitularId */
  serial: string;
  /** Serial técnico del certificado (serialNumber), si viene */
  serialNumber: string;
  validoHasta: string;
  estado: string;
}

@Component({
  selector: 'app-reset-step-certificados',
  templateUrl: './step-certificados.component.html',
  styleUrls: ['./step-certificados.component.css'],
  standalone: false
})
export class StepCertificadosComponent implements OnInit {
  @Output() next = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();
  @Output() cancelar = new EventEmitter<void>();

  usuario = { nombre: '', cantidadTokens: 0 };
  certificados: Certificado[] = [];
  oportunidades = 0;
  datosVerificacion: any = null;
  showPasswordForm = false;
  showNueva = false;
  showConfirmar = false;
  form!: FormGroup;
  readonly passwordMaxLength = PASSWORD_MAX_LENGTH;

  constructor(
    private fb: FormBuilder,
    private resetPassword: ResetPasswordService,
    private flowContext: ResetPasswordFlowContext,
    private uiHelper: UiHelperService,
    private notification: NotificationService
  ) {}

  get isCcsw5(): boolean {
    return this.flowContext.isCcsw5;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      nueva: ['', this.isCcsw5 ? ccsw5PasswordValidators() : tokenVirtualPasswordValidators()],
      confirmar: ['', Validators.required]
    });
    const raw = this.resetPassword.getStorage('datosVerificacion');
    if (raw) {
      try {
        this.datosVerificacion = JSON.parse(raw);
        this.usuario.nombre = [(this.datosVerificacion.nombres || ''), (this.datosVerificacion.apellidos || '')].join(' ').trim().toUpperCase() || 'Usuario';
      } catch (_) {}
    }
    this.obtenerInformacionUsuario();
  }

  obtenerInformacionUsuario(): void {
    if (!this.datosVerificacion) return;
    const request = {
      tipoIdentificacionId: parseInt(this.datosVerificacion.tipoIdentificacion, 10),
      numeroIdentificacion: this.datosVerificacion.numeroIdentificacion
    };
    this.uiHelper.showSpinner('Cargando información...', 'admin');
    this.resetPassword.informacionUsuario(request).pipe(
      finalize(() => this.uiHelper.hideSpinner())
    ).subscribe({
      next: (res) => {
        const data = this.normalizeInformacionUsuario(res);
        if (data) {
          if (data.ordenPedidoId != null) this.resetPassword.setStorage('ordenPedidoId', String(data.ordenPedidoId));
          if (data.solicitudTitularId != null) this.resetPassword.setStorage('solicitudTitularId', String(data.solicitudTitularId));
          this.usuario.cantidadTokens = data.cantidad ?? 0;
          this.oportunidades = data.intentos !== undefined && data.intentos !== null ? data.intentos : 10;
          this.certificados = data.certificados?.length
            ? this.mapearYFiltrarVigentes(data.certificados)
            : [];
          // Si el root no trae IDs, tomarlos del primer certificado (CCSW5)
          if (!this.resetPassword.getStorage('ordenPedidoId') || !this.resetPassword.getStorage('solicitudTitularId')) {
            const first = data.certificados?.[0];
            if (first?.ordenPedidoId != null) this.resetPassword.setStorage('ordenPedidoId', String(first.ordenPedidoId));
            if (first?.solicitudTitularId != null) this.resetPassword.setStorage('solicitudTitularId', String(first.solicitudTitularId));
          }
        }
      },
      error: (_) => {
        this.usuario.cantidadTokens = 0;
        this.oportunidades = 0;
        this.certificados = [];
      }
    });
  }

  /** Acepta payload plano o anidado en `data` (objeto o JSON string). */
  private normalizeInformacionUsuario(res: any): InformacionUsuarioData | null {
    let payload = res?.data ?? res;
    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch {
        return null;
      }
    }
    if (payload?.data && (typeof payload.data === 'object' || typeof payload.data === 'string')) {
      let inner = payload.data;
      if (typeof inner === 'string') {
        try {
          inner = JSON.parse(inner);
        } catch {
          return payload as InformacionUsuarioData;
        }
      }
      if (inner && (inner.certificados != null || inner.cantidad != null)) {
        return inner as InformacionUsuarioData;
      }
    }
    return (payload as InformacionUsuarioData) || null;
  }

  mapearYFiltrarVigentes(list: any[]): Certificado[] {
    return this.flowContext.isCcsw5
      ? this.mapearYFiltrarVigentesCcsw5(list)
      : this.mapearYFiltrarVigentesTokenVirtual(list);
  }

  /** Certifirma Digital Virtual clásico: approvalConceptDate + validity (días). */
  private mapearYFiltrarVigentesTokenVirtual(list: any[]): Certificado[] {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return list
      .map(c => {
        const validoHastaStr = this.calcularVencimiento(c.approvalConceptDate, c.validity);
        const fechaVencimiento = this.obtenerFechaVencimiento(c.approvalConceptDate, c.validity);
        const vigente = fechaVencimiento !== null && fechaVencimiento >= hoy;
        const cn = c.cn || this.usuario.nombre;
        return {
          cn,
          titulo: c.titulo || '',
          nombre: cn,
          politica: c.certPolicy || '',
          serial: c.serialNumber || '',
          serialNumber: c.serialNumber || '',
          validoHasta: validoHastaStr,
          estado: this.estadoCert(c.certStatus),
          vigente
        };
      })
      .filter(c => c.vigente)
      .map(({ vigente: _, ...cert }) => cert);
  }

  /**
   * CCSW5: cn, serial number = solicitudTitularId, titulo, fechaValidez.
   */
  private mapearYFiltrarVigentesCcsw5(list: any[]): Certificado[] {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return list
      .map(c => {
        const fechaVencimiento = this.parseFechaDiaMesAnio(c.fechaValidez);
        const vigente =
          fechaVencimiento !== null &&
          fechaVencimiento >= hoy &&
          this.esCertificadoEmitidoCcsw5(c);
        const cn = c.cn || [c.nombre, c.apellido].filter(Boolean).join(' ').trim() || this.usuario.nombre;
        return {
          cn,
          titulo: c.titulo || '',
          nombre: cn,
          politica: c.tipo || c.unidadOrganizacional || c.certPolicy || '',
          serial: c.solicitudTitularId != null ? String(c.solicitudTitularId) : '',
          serialNumber: c.serialNumber || c.username || '',
          validoHasta: c.fechaValidez || 'N/A',
          estado: this.estadoCert(c.certStatus || c.estado),
          vigente
        };
      })
      .filter(c => c.vigente)
      .map(({ vigente: _, ...cert }) => cert);
  }

  private esCertificadoEmitidoCcsw5(c: any): boolean {
    const status = String(c?.certStatus || '').toUpperCase();
    if (status.includes('REVOK') || status.includes('INACTIVE') || status.includes('REVOC')) {
      return false;
    }
    if (c?.success === 0 || c?.success === '0') {
      return false;
    }
    return true;
  }

  /** Parsea fechas dd/MM/yyyy (con o sin hora). */
  private parseFechaDiaMesAnio(value: string | null | undefined): Date | null {
    if (!value) return null;
    try {
      const [fecha] = String(value).trim().split(' ');
      const parts = fecha.split('/');
      if (parts.length !== 3) return null;
      const [d, m, a] = parts;
      const date = new Date(parseInt(a, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      if (Number.isNaN(date.getTime())) return null;
      date.setHours(0, 0, 0, 0);
      return date;
    } catch {
      return null;
    }
  }

  private obtenerFechaVencimiento(creationDate: string, validity: number): Date | null {
    if (!creationDate || validity == null) return null;
    try {
      const [fecha] = creationDate.split(' ');
      const [d, m, a] = fecha.split('/');
      const from = new Date(parseInt(a, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      const to = new Date(from);
      to.setDate(to.getDate() + validity);
      to.setHours(0, 0, 0, 0);
      return to;
    } catch (_) {
      return null;
    }
  }

  calcularVencimiento(creationDate: string, validity: number): string {
    if (!creationDate || !validity) return 'N/A';
    try {
      const [fecha] = creationDate.split(' ');
      const [d, m, a] = fecha.split('/');
      const from = new Date(parseInt(a, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      const to = new Date(from);
      to.setDate(to.getDate() + validity);
      return to.toLocaleDateString('es-CO');
    } catch (_) { return 'N/A'; }
  }

  estadoCert(status: string): string {
    const s = (status || '').toLowerCase();
    if (s.includes('revok') || s.includes('inactive') || s.includes('revoc')) return 'Inactivo';
    return 'Activo';
  }

  mostrarFormularioContrasena(): void {
    if (this.oportunidades <= 0) {
      Swal.fire({
        title: 'Sin oportunidades',
        text: 'Has alcanzado el máximo de intentos para recuperar la contraseña. Debes solicitar reposición o nuevo certificado.',
        icon: 'warning',
        ...this.notification.getSwalBaseConfig()
      });
      return;
    }
    this.showPasswordForm = true;
  }

  hasError(campo: string): boolean {
    const c = this.form.get(campo);
    return !!(c && c.invalid && (c.touched || c.dirty));
  }

  getErrorMessage(campo: string): string {
    const c = this.form.get(campo);
    if (!c || !c.errors) return '';
    if (c.errors['required']) return 'Campo obligatorio';
    if (c.errors['minlength']) {
      return this.isCcsw5 ? 'Mínimo 10 caracteres' : 'Mínimo 8 caracteres';
    }
    if (c.errors['maxlength']) return 'Máximo 12 caracteres';
    if (c.errors['pattern']) {
      return this.isCcsw5
        ? 'Debe tener 10–12 caracteres, mayúscula, número y carácter especial'
        : 'Debe incluir mayúscula, número y carácter especial';
    }
    return '';
  }

  get passwordNueva(): string {
    return this.form.get('nueva')?.value || '';
  }

  passwordsMatch(): boolean {
    const n = this.form.get('nueva')?.value;
    const c = this.form.get('confirmar')?.value;
    return !!n && n === c;
  }

  get hasMinLength(): boolean {
    return this.isCcsw5
      ? passwordHasCcsw5Length(this.passwordNueva)
      : passwordHasMinLength(this.passwordNueva);
  }
  get hasUpper(): boolean { return passwordHasUpper(this.passwordNueva); }
  get hasNumber(): boolean { return passwordHasNumber(this.passwordNueva); }
  get hasSpecial(): boolean { return passwordHasSpecial(this.passwordNueva); }

  ngSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (!this.passwordsMatch()) {
      Swal.fire({ title: 'Aviso', text: 'Las contraseñas no coinciden.', icon: 'warning', ...this.notification.getSwalBaseConfig() });
      return;
    }
    this.cambiarContrasena();
  }

  cambiarContrasena(): void {
    if (!this.datosVerificacion) {
      Swal.fire({ title: 'Error', text: 'Faltan datos.', icon: 'error', ...this.notification.getSwalBaseConfig() });
      return;
    }
    const ordenPedidoId = this.resetPassword.getStorage('ordenPedidoId');
    const solicitudTitularId = this.resetPassword.getStorage('solicitudTitularId');
    if (!ordenPedidoId || !solicitudTitularId) {
      Swal.fire({ title: 'Error', text: 'Falta información de orden.', icon: 'error', ...this.notification.getSwalBaseConfig() });
      return;
    }
    const request = {
      tipoIdentificacionId: parseInt(this.datosVerificacion.tipoIdentificacion, 10),
      numeroIdentificacion: this.datosVerificacion.numeroIdentificacion,
      passwordNuevo: this.form.value.nueva,
      ordenPedidoId: parseInt(ordenPedidoId, 10),
      solicitudTitularId: parseInt(solicitudTitularId, 10)
    };
    this.uiHelper.showSpinner('Cambiando contraseña...', 'admin');
    const baseConfig = this.notification.getSwalBaseConfig();
    this.resetPassword.cambiarContrasena(request).pipe(
      finalize(() => this.uiHelper.hideSpinner())
    ).subscribe({
      next: (res) => {
        const data = res?.data || (res as any);
        if (data?.exitoso === true) {
          this.resetPassword.clearFlowStorage();
          this.next.emit();
        } else {
          Swal.fire({ title: 'Error', text: data?.mensaje || 'No se pudo cambiar la contraseña.', icon: 'error', ...baseConfig });
        }
      },
      error: (err) => {
        const msg = (err && (err as any).message) || err?.error?.mensaje || err?.error?.message || 'No se pudo cambiar la contraseña.';
        Swal.fire({
          title: 'Error',
          text: msg,
          icon: 'error',
          ...this.notification.getSwalBaseConfigReloadAfterClose()
        });
      }
    });
  }

  onCancelar(): void {
    this.resetPassword.clearFlowStorage();
    this.cancelar.emit();
  }
}
